import { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'sonner';

/**
 * Auto-save hook for forms and data editing
 * @param {Object} data - The data to auto-save
 * @param {Function} saveFunction - Async function to save the data
 * @param {Object} options - Configuration options
 * @param {number} options.delay - Debounce delay in ms (default: 1500)
 * @param {boolean} options.enabled - Whether auto-save is enabled (default: true)
 * @param {Function} options.onSaveStart - Callback when save starts
 * @param {Function} options.onSaveSuccess - Callback when save succeeds
 * @param {Function} options.onSaveError - Callback when save fails
 * @param {boolean} options.showToast - Whether to show toast notifications (default: false)
 */
export function useAutoSave(data, saveFunction, options = {}) {
  const {
    delay = 1500,
    enabled = true,
    onSaveStart,
    onSaveSuccess,
    onSaveError,
    showToast = false,
  } = options;

  const [saveStatus, setSaveStatus] = useState('idle'); // 'idle' | 'saving' | 'saved' | 'error'
  const [lastSaved, setLastSaved] = useState(null);
  const timeoutRef = useRef(null);
  const initialDataRef = useRef(null);
  const lastSavedDataRef = useRef(null);

  // Store initial data on mount
  useEffect(() => {
    if (initialDataRef.current === null && data) {
      initialDataRef.current = JSON.stringify(data);
      lastSavedDataRef.current = JSON.stringify(data);
    }
  }, []);

  // Check if data has changed from last saved state
  const hasChanges = useCallback(() => {
    if (!data || !lastSavedDataRef.current) return false;
    return JSON.stringify(data) !== lastSavedDataRef.current;
  }, [data]);

  // Save function with debouncing
  const save = useCallback(async () => {
    if (!saveFunction || !hasChanges()) return;

    setSaveStatus('saving');
    onSaveStart?.();

    try {
      await saveFunction(data);
      setSaveStatus('saved');
      setLastSaved(new Date());
      lastSavedDataRef.current = JSON.stringify(data);
      onSaveSuccess?.();
      
      if (showToast) {
        toast.success('Changes saved', { duration: 2000 });
      }

      // Reset to idle after showing "saved" status
      setTimeout(() => {
        setSaveStatus('idle');
      }, 2000);
    } catch (error) {
      setSaveStatus('error');
      onSaveError?.(error);
      
      if (showToast) {
        toast.error('Failed to save changes');
      }

      // Reset to idle after showing error
      setTimeout(() => {
        setSaveStatus('idle');
      }, 3000);
    }
  }, [data, saveFunction, hasChanges, onSaveStart, onSaveSuccess, onSaveError, showToast]);

  // Debounced auto-save effect
  useEffect(() => {
    if (!enabled || !hasChanges()) return;

    // Clear existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Set new timeout for debounced save
    timeoutRef.current = setTimeout(() => {
      save();
    }, delay);

    // Cleanup
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [data, enabled, delay, save, hasChanges]);

  // Manual save function (bypasses debounce)
  const saveNow = useCallback(async () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    await save();
  }, [save]);

  // Reset function
  const reset = useCallback(() => {
    lastSavedDataRef.current = JSON.stringify(data);
    setSaveStatus('idle');
  }, [data]);

  return {
    saveStatus,
    lastSaved,
    hasChanges: hasChanges(),
    saveNow,
    reset,
  };
}

/**
 * Auto-save status indicator component
 */
export function AutoSaveIndicator({ status, lastSaved, className = '' }) {
  const getStatusDisplay = () => {
    switch (status) {
      case 'saving':
        return (
          <span className="flex items-center gap-1.5 text-amber-500 text-sm">
            <svg className="h-3 w-3 animate-spin" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
            Saving...
          </span>
        );
      case 'saved':
        return (
          <span className="flex items-center gap-1.5 text-emerald-500 text-sm">
            <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            Saved
          </span>
        );
      case 'error':
        return (
          <span className="flex items-center gap-1.5 text-red-500 text-sm">
            <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
            Error saving
          </span>
        );
      default:
        return lastSaved ? (
          <span className="text-slate-400 text-sm">
            Last saved {new Date(lastSaved).toLocaleTimeString()}
          </span>
        ) : null;
    }
  };

  return (
    <div className={`transition-opacity duration-200 ${className}`}>
      {getStatusDisplay()}
    </div>
  );
}

export default useAutoSave;
